import * as React from "react";

import { cn } from "@/lib/utils";

import { IconTile } from "./icon-tile";

type EmptyStateProps = Omit<React.ComponentProps<"div">, "title"> & {
  icon?: React.ComponentType<{ className?: string }>;
  /** Drawn in place of the icon tile, for a brand mark that is its own tile. */
  media?: React.ReactNode;
  /** Title case, 13px weight 500. */
  title: React.ReactNode;
  /** Sentence case, 12px `fg-3`. */
  description?: React.ReactNode;
  /** At most one primary action. */
  action?: React.ReactNode;
  /** Pill suggestions or secondary links under the action. */
  children?: React.ReactNode;
  /** `inline` sits left-aligned inside a card; `centered` fills a pane. */
  layout?: "centered" | "inline";
};

/** An icon tile plus text. No illustrations. */
function EmptyState({
  icon,
  media,
  title,
  description,
  action,
  layout = "centered",
  className,
  children,
  ...props
}: EmptyStateProps) {
  const centered = layout === "centered";
  return (
    <div
      data-slot="empty-state"
      className={cn(
        "flex min-w-0 flex-col gap-3",
        centered
          ? "flex-1 items-center justify-center p-6 text-center"
          : "items-start p-4",
        className
      )}
      {...props}
    >
      {media ?? (icon ? <IconTile icon={icon} size="lg" /> : null)}
      <div
        className={cn(
          "flex max-w-sm flex-col gap-1",
          centered && "items-center"
        )}
      >
        <div className="text-base font-medium text-fg">{title}</div>
        {description ? (
          <div className="text-sm text-balance text-fg-3">{description}</div>
        ) : null}
      </div>
      {action ? (
        <div className="flex items-center gap-1.5">{action}</div>
      ) : null}
      {children ? (
        <div
          className={cn(
            "flex max-w-md flex-wrap gap-1.5",
            centered && "justify-center"
          )}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

export { EmptyState };
export type { EmptyStateProps };
